import { useCallback, useEffect, useRef } from 'react';
import { ConversationMessageDto } from '@endion/types';
import { isMobile } from 'is-mobile';
import { useChatContext } from '../../hooks/use-chat-context';
import {
  useRegisterViewportSizeListeners,
  UseRegisterViewportSizeListenersParams,
} from '../../hooks/use-register-viewport-size-listeners.hook';
import { AssistantChatLoadingIndicator } from '../common/assistant-chat-loading-indicator';
import { ChatMessage } from './chat-message';

export type ChatMessageListProps = {
  messages: ConversationMessageDto[];
  isLoading: boolean;
  isLoadingContext: boolean;
};
export const ChatMessageList = ({ messages, isLoading, isLoadingContext }: ChatMessageListProps) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const currentViewportHeight = useRef<number>(window?.visualViewport?.height || window?.innerHeight || 0);
  const prevViewportHeight = useRef<number>(window?.visualViewport?.height || window?.innerHeight || 0);
  const { isOpen } = useChatContext();

  // Handle scrolling behavior for incoming messages
  // It scrolls the list to the bottom while receiving streamed messages.
  useEffect(() => {
    if (!messagesEndRef.current) return;
    messagesEndRef.current.scrollIntoView({ behavior: 'auto' });
  }, [messages]);

  // Scroll to latest message when the chatbot opens
  useEffect(() => {
    if (!isOpen || !messagesEndRef.current) return;
    messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [isOpen]);

  // Handle scrolling behavior for open/close on-screen-keyboard (mobile)
  // It will scroll the list to the bottom right after resizing the screen
  const onViewportChange: UseRegisterViewportSizeListenersParams['onViewportChange'] = useCallback((s) => {
    prevViewportHeight.current = currentViewportHeight.current;
    currentViewportHeight.current = s.height;
    if (
      isMobile({ tablet: true, featureDetect: true }) &&
      prevViewportHeight.current !== currentViewportHeight.current
    ) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
    }
  }, []);
  useRegisterViewportSizeListeners({
    onViewportChange,
  });

  return (
    <div className="flex flex-col p-5 overflow-y-scroll space-y-2 flex-grow" id="test-id">
      {messages.map((message, index) => (
        <ChatMessage message={message} messages={messages} index={index} isLoading={isLoading} key={message.id} />
      ))}
      <AssistantChatLoadingIndicator
        shouldRender={(isLoading && messages.at(-1)?.role === 'user') || isLoadingContext}
      />
      <div className="end-ref" ref={messagesEndRef} />
    </div>
  );
};
